/**
 * server functions حصريّة للأدمن — تستخدم supabaseAdmin لتجاوز RLS عند الحاجة،
 * مع التحقّق الإجباريّ من دور admin قبل أيّ عمليّة. لا تُستدعى من واجهات السفير العاديّة.
 *
 * الوظائف:
 *   - listAmbassadorsAdmin     قائمة السفراء + الأرصدة + إحصاءات سريعة
 *   - updateAmbassadorAdmin    تعديل status / tier / custom_reward_rate / payout_mode / threshold
 *   - listPayoutsAdmin         طلبات الدفع المعلّقة + المعالَجة
 *   - processPayoutAdmin       اعتماد/رفض طلب دفع + تسجيل cash_payout
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { TablesUpdate } from "@/integrations/supabase/types";
import { assertAdmin } from "./adminAuth.server";

// ─────────────────────────────────────────────────────────────────────────────
// 1) قائمة السفراء — مع الأرصدة وعدد الطلبات
// ─────────────────────────────────────────────────────────────────────────────
export const listAmbassadorsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    await assertAdmin(userId);

    // كل السفراء عبر admin client
    const { data: ambassadors, error } = await supabaseAdmin
      .from("ambassadors")
      .select(
        "id, user_id, code, display_name, status, tier, payout_mode, custom_reward_rate, custom_payout_threshold, custom_cookie_days, instagram, tiktok, bio, created_at, approved_at, relationship_type, contract_starts_at, contract_ends_at, monthly_fee_aed, contract_currency, deliverables_note, seeding_value_aed, external_channel, uppromote_affiliate_id, external_email, profile_status",
      )
      .order("created_at", { ascending: false });


    if (error) {
      console.error("[listAmbassadorsAdmin]", error);
      return { ambassadors: [] };
    }

    if (!ambassadors || ambassadors.length === 0) return { ambassadors: [] };

    const ambIds = ambassadors.map((a) => a.id);
    const userIds = ambassadors.map((a) => a.user_id).filter((x): x is string => !!x);

    // الأرصدة
    const { data: balances } = await supabaseAdmin
      .from("ambassador_balances")
      .select("ambassador_id, currency, available_credit, pending_credit, paid_out")
      .in("ambassador_id", ambIds);

    // إحصاءات سريعة: عدد المبيعات لكلّ سفير
    const { data: events } = await supabaseAdmin
      .from("ambassador_events")
      .select("ambassador_id, event_type, order_total")
      .in("ambassador_id", ambIds);

    // إيميلات (auth.users) — بنداء جماعيّ admin
    const emailsByUser = new Map<string, string | null>();
    await Promise.all(
      userIds.map(async (uid) => {
        try {
          const { data } = await supabaseAdmin.auth.admin.getUserById(uid);
          emailsByUser.set(uid, data?.user?.email ?? null);
        } catch {
          emailsByUser.set(uid, null);
        }
      }),
    );

    // ── مشترياته الشخصيّة (طلبات هذا الشخص نفسه، لا مبيعات كوده) ──────────────
    // مصدر واحد للتصنيف: customerOrders.server (نفس ما يستخدمه الملفّ الكامل).
    const { unionOrders, summarizeOrders, emptySummary, emailOrFilter } = await import(
      "@/lib/server/customerOrders.server"
    );

    const allEmails = new Set<string>();
    for (const a of ambassadors) {
      const em = a.user_id ? emailsByUser.get(a.user_id) : null;
      if (em) allEmails.add(em.toLowerCase());
      if (a.external_email) allEmails.add(a.external_email.toLowerCase());
    }

    const ordersSelect =
      "id, shopify_order_id, order_name, user_id, customer_email, total_amount, currency, financial_status, shopify_created_at, created_at, cancelled_at";
    const emailFilter = emailOrFilter(allEmails);
    const [byUserRes, byEmailRes] = await Promise.all([
      userIds.length
        ? supabaseAdmin.from("orders").select(ordersSelect).in("user_id", userIds)
        : Promise.resolve({ data: [] as never[], error: null }),
      emailFilter
        ? supabaseAdmin.from("orders").select(ordersSelect).or(emailFilter)
        : Promise.resolve({ data: [] as never[], error: null }),
    ]);
    for (const res of [byUserRes, byEmailRes]) {
      const err = (res as { error?: { message?: string } }).error;
      if (err?.message) throw new Response(err.message, { status: 500 });
    }

    type OrderRow = Record<string, unknown>;
    const allOrders = unionOrders(
      ((byUserRes.data ?? []) as OrderRow[]) as never,
      ((byEmailRes.data ?? []) as OrderRow[]) as never,
    );
    const ordersByUser = new Map<string, OrderRow[]>();
    const ordersByEmail = new Map<string, OrderRow[]>();
    for (const row of allOrders as unknown as OrderRow[]) {
      const uid = row["user_id"] ? String(row["user_id"]) : null;
      if (uid) {
        const arr = ordersByUser.get(uid) ?? [];
        arr.push(row);
        ordersByUser.set(uid, arr);
      }
      const em = row["customer_email"] ? String(row["customer_email"]).toLowerCase() : null;
      if (em) {
        const arr = ordersByEmail.get(em) ?? [];
        arr.push(row);
        ordersByEmail.set(em, arr);
      }
    }

    type Stats = { sales: number; revenue: number; clicks: number };
    const statsByAmb = new Map<string, Stats>();
    for (const a of ambIds) statsByAmb.set(a, { sales: 0, revenue: 0, clicks: 0 });
    for (const ev of events ?? []) {
      const s = statsByAmb.get(ev.ambassador_id);
      if (!s) continue;
      if (ev.event_type === "click") s.clicks += 1;
      if (ev.event_type === "purchase") {
        s.sales += 1;
        s.revenue += Number(ev.order_total ?? 0);
      }
    }

    type BalanceRow = {
      currency: string;
      available_credit: number;
      pending_credit: number;
      paid_out: number;
    };
    const balancesByAmb = new Map<string, BalanceRow[]>();
    for (const b of balances ?? []) {
      const arr = balancesByAmb.get(b.ambassador_id) ?? [];
      arr.push({
        currency: b.currency,
        available_credit: Number(b.available_credit ?? 0),
        pending_credit: Number(b.pending_credit ?? 0),
        paid_out: Number(b.paid_out ?? 0),
      });
      balancesByAmb.set(b.ambassador_id, arr);
    }

    return {
      ambassadors: ambassadors.map((a) => {
        const email = a.user_id ? (emailsByUser.get(a.user_id) ?? null) : null;
        const emailKey = (email ?? a.external_email ?? "").toLowerCase();
        // دمج طلبات الحساب + طلبات البريد (الضيف قبل الربط) في قائمة فريدة
        const merged = unionOrders(
          (a.user_id ? ordersByUser.get(a.user_id) : undefined) as never,
          (emailKey ? ordersByEmail.get(emailKey) : undefined) as never,
        );
        const purchases = merged.length ? summarizeOrders(merged) : emptySummary();
        return {
          ...a,
          email,
          balances: balancesByAmb.get(a.id) ?? [],
          stats: statsByAmb.get(a.id) ?? { sales: 0, revenue: 0, clicks: 0 },
          purchases,
        };
      }),
    };

  });

// ─────────────────────────────────────────────────────────────────────────────
// 2) تعديل سفير
// ─────────────────────────────────────────────────────────────────────────────
const updateAmbassadorInput = z.object({
  ambassadorId: z.string().uuid(),
  status: z.enum(["pending", "approved", "rejected", "suspended"]).optional(),
  tier: z.enum(["decouverte", "active", "elite"]).optional(),
  payout_mode: z.enum(["none", "cash", "store_credit", "both"]).optional(),
  custom_reward_rate: z.number().min(0).max(100).nullable().optional(),
  custom_payout_threshold: z.number().min(0).nullable().optional(),
  // مدّة كوكي تتبّع الإحالة بالأيّام (1-120). NULL = استخدم الافتراضي 30.
  custom_cookie_days: z.number().int().min(1).max(120).nullable().optional(),
  external_channel: z.enum(["direct", "uppromote"]).optional(),
  uppromote_affiliate_id: z.string().max(255).nullable().optional(),
});
export type UpdateAmbassadorInput = z.infer<typeof updateAmbassadorInput>;

export const updateAmbassadorAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => updateAmbassadorInput.parse(input))
  .handler(async ({ context, data }) => {
    const { userId } = context;
    await assertAdmin(userId);

    const updates: TablesUpdate<"ambassadors"> = { updated_at: new Date().toISOString() };
    if (data.status !== undefined) {
      updates.status = data.status;
      if (data.status === "approved") {
        updates.approved_at = new Date().toISOString();
        updates.approved_by = userId;
      }
    }
    if (data.tier !== undefined) updates.tier = data.tier;
    if (data.payout_mode !== undefined) updates.payout_mode = data.payout_mode;
    if (data.custom_reward_rate !== undefined) updates.custom_reward_rate = data.custom_reward_rate;
    if (data.custom_payout_threshold !== undefined)
      updates.custom_payout_threshold = data.custom_payout_threshold;
    if (data.custom_cookie_days !== undefined)
      updates.custom_cookie_days = data.custom_cookie_days;
    if (data.external_channel !== undefined) updates.external_channel = data.external_channel;
    if (data.uppromote_affiliate_id !== undefined)
      updates.uppromote_affiliate_id = data.uppromote_affiliate_id;

    const { error } = await supabaseAdmin
      .from("ambassadors")
      .update(updates)
      .eq("id", data.ambassadorId);

    if (error) {
      console.error("[updateAmbassadorAdmin]", error);
      throw new Response(error.message, { status: 400 });
    }
    return { ok: true };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 3) قائمة طلبات الدفع
// ─────────────────────────────────────────────────────────────────────────────
export const listPayoutsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    await assertAdmin(userId);

    const { data: payouts, error } = await supabaseAdmin
      .from("ambassador_payouts")
      .select(
        "id, ambassador_id, amount, currency, method, status, note, requested_at, processed_at",
      )
      .order("requested_at", { ascending: false })
      .limit(200);

    if (error) {
      console.error("[listPayoutsAdmin]", error);
      return { payouts: [] };
    }
    if (!payouts || payouts.length === 0) return { payouts: [] };

    const ambIds = Array.from(new Set(payouts.map((p) => p.ambassador_id)));
    const { data: ambs } = await supabaseAdmin
      .from("ambassadors")
      .select("id, display_name, code, tier")
      .in("id", ambIds);

    const ambMap = new Map<string, { display_name: string; code: string; tier: string }>();
    for (const a of ambs ?? []) ambMap.set(a.id, { display_name: a.display_name, code: a.code, tier: a.tier });

    return {
      payouts: payouts.map((p) => ({
        ...p,
        amount: Number(p.amount),
        ambassador_name: ambMap.get(p.ambassador_id)?.display_name ?? "—",
        ambassador_code: ambMap.get(p.ambassador_id)?.code ?? "—",
        ambassador_tier: ambMap.get(p.ambassador_id)?.tier ?? "decouverte",
      })),
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 4) معالجة طلب دفع — موافقة (يدرج cash_payout) أو رفض
// ─────────────────────────────────────────────────────────────────────────────
const processPayoutInput = z.object({
  payoutId: z.string().uuid(),
  action: z.enum(["mark_processing", "approve", "reject"]),
  note: z.string().max(500).optional(),
});
export type ProcessPayoutInput = z.infer<typeof processPayoutInput>;

export const processPayoutAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => processPayoutInput.parse(input))
  .handler(async ({ context, data }) => {
    const { userId } = context;
    await assertAdmin(userId);

    // Status and money are committed by one database transaction. Never fall
    // back to the old split writes when the migration has not been applied.
    const { data: rows, error } = await supabaseAdmin.rpc("resolve_payout_atomic", {
      _actor_user_id: userId,
      _payout_id: data.payoutId,
      _action: data.action,
      _note: data.note,
    });
    if (error) {
      console.error("[processPayoutAdmin] atomic resolution failed", error.message);
      const message = error.message;
      const status = message.includes("PAYOUT_NOT_FOUND") ? 404
        : message.includes("ADMIN_FORBIDDEN") ? 403
        : message.includes("PAYOUT_ALREADY_FINALIZED") || message.includes("PAYOUT_LEDGER_REVIEW_REQUIRED") ? 409
        : 503;
      throw new Response("تعذّر تحديث طلب السحب. لم تُعتمد العملية؛ راجع الحالة قبل المحاولة مجدداً.", { status });
    }
    const payout = rows?.[0];
    if (!payout || !["processing", "paid", "rejected"].includes(payout.payout_status)) {
      throw new Response("تعذّر التحقّق من نتيجة طلب السحب.", { status: 503 });
    }
    const action = payout.payout_status as "processing" | "paid" | "rejected";
    // Notification errors cannot undo money that has already committed. A
    // duplicate administrative retry must not send a second notification.
    if (!payout.was_idempotent) {
      try {
        const { notifyWalletEvent } = await import("@/lib/server/walletNotifier");
        await notifyWalletEvent({
          ambassadorId: payout.ambassador_id,
          kind: action === "processing" ? "payout_processing" : action === "paid" ? "payout_paid" : "payout_rejected",
          amount: Number(payout.amount),
          currency: payout.currency,
          metadata: { payout_id: data.payoutId },
          ...(action === "rejected" ? { rejectionReason: data.note } : {}),
        });
      } catch (e) { console.error("[processPayoutAdmin] notification failed", e); }
    }
    return { ok: true, action };

  });

// ─────────────────────────────────────────────────────────────────────────────
// 5) قائمة قواعد المكافآت العامّة (ambassador_reward_rules)
// ─────────────────────────────────────────────────────────────────────────────
export const listRewardRulesAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);

    const { data, error } = await supabaseAdmin
      .from("ambassador_reward_rules")
      .select(
        "id, event_type, tier, reward_type, value, currency, min_order_value, max_reward_per_event, max_orders_per_referral, reward_window_days, applicable_markets, active_from, active_to, campaign_name, note, value_per_market, min_order_per_market, max_reward_per_market",
      )
      .order("tier", { ascending: true })
      .order("event_type", { ascending: true })
      .order("active_from", { ascending: false });

    if (error) {
      console.error("[listRewardRulesAdmin]", error);
      return { rules: [] };
    }

    return {
      rules: (data ?? []).map((r) => ({
        ...r,
        value: Number(r.value),
        min_order_value: r.min_order_value !== null ? Number(r.min_order_value) : null,
        max_reward_per_event: r.max_reward_per_event !== null ? Number(r.max_reward_per_event) : null,
        value_per_market: (r as any).value_per_market ?? null,
        min_order_per_market: (r as any).min_order_per_market ?? null,
        max_reward_per_market: (r as any).max_reward_per_market ?? null,
      })),
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 6) تحديث قاعدة مكافأة موجودة (upsert على id)
// ─────────────────────────────────────────────────────────────────────────────
const perMarketSchema = z
  .object({
    AED: z.number().min(0).optional(),
    SAR: z.number().min(0).optional(),
    USD: z.number().min(0).optional(),
    OMR: z.number().min(0).optional(),
    BRL: z.number().min(0).optional(),
  })
  .nullable()
  .optional();

const upsertRewardRuleInput = z.object({
  id: z.string().uuid(),
  reward_type: z.enum(["percent", "fixed"]).optional(),
  value: z.number().min(0).optional(),
  currency: z.string().length(3).nullable().optional(),
  min_order_value: z.number().min(0).nullable().optional(),
  max_reward_per_event: z.number().min(0).nullable().optional(),
  max_orders_per_referral: z.number().int().min(0).nullable().optional(),
  reward_window_days: z.number().int().min(0).nullable().optional(),
  applicable_markets: z.array(z.string()).nullable().optional(),
  campaign_name: z.string().max(120).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
  value_per_market: perMarketSchema,
  min_order_per_market: perMarketSchema,
  max_reward_per_market: perMarketSchema,
});
export type UpsertRewardRuleInput = z.infer<typeof upsertRewardRuleInput>;

export const upsertRewardRuleAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => upsertRewardRuleInput.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);

    const updates: TablesUpdate<"ambassador_reward_rules"> = {};
    if (data.reward_type !== undefined) updates.reward_type = data.reward_type;
    if (data.value !== undefined) updates.value = data.value;
    if (data.currency !== undefined) updates.currency = data.currency;
    if (data.min_order_value !== undefined) updates.min_order_value = data.min_order_value;
    if (data.max_reward_per_event !== undefined)
      updates.max_reward_per_event = data.max_reward_per_event;
    if (data.max_orders_per_referral !== undefined)
      updates.max_orders_per_referral = data.max_orders_per_referral;
    if (data.reward_window_days !== undefined)
      updates.reward_window_days = data.reward_window_days;
    if (data.applicable_markets !== undefined)
      updates.applicable_markets = data.applicable_markets;
    if (data.campaign_name !== undefined) updates.campaign_name = data.campaign_name;
    if (data.note !== undefined) updates.note = data.note;
    if (data.value_per_market !== undefined)
      (updates as any).value_per_market = data.value_per_market;
    if (data.min_order_per_market !== undefined)
      (updates as any).min_order_per_market = data.min_order_per_market;
    if (data.max_reward_per_market !== undefined)
      (updates as any).max_reward_per_market = data.max_reward_per_market;

    const { error } = await supabaseAdmin
      .from("ambassador_reward_rules")
      .update(updates)
      .eq("id", data.id);

    if (error) {
      console.error("[upsertRewardRuleAdmin]", error);
      throw new Response(error.message, { status: 400 });
    }
    return { ok: true };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 7) قراءة كلّ مفاتيح app_settings (للأدمن فقط)
// ─────────────────────────────────────────────────────────────────────────────
export const listAppSettingsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("app_settings")
      .select("key, value, description, updated_at")
      .order("key", { ascending: true });
    if (error) {
      console.error("[listAppSettingsAdmin]", error);
      return { settings: [] };
    }
    return { settings: data ?? [] };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 8) تعديل قيمة مفتاح في app_settings
// ─────────────────────────────────────────────────────────────────────────────
const updateAppSettingInput = z.object({
  key: z.string().min(1).max(100),
  value: z.unknown(), // jsonb — أيّ نوع
});
export type UpdateAppSettingInput = z.infer<typeof updateAppSettingInput>;

export const updateAppSettingAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => updateAppSettingInput.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("app_settings")
      .update({
        value: data.value as never,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      })
      .eq("key", data.key);
    if (error) {
      console.error("[updateAppSettingAdmin]", error);
      throw new Response(error.message, { status: 400 });
    }
    // إفراغ cache الإعدادات الرقميّة فوراً ليسري التغيير دون انتظار TTL
    const { invalidateAppSettingsCache } = await import("@/lib/server/appSettings");
    invalidateAppSettingsCache(data.key);
    // إعدادات حارس جودة التسويق لها cache منفصل — أبطله أيضاً
    if (data.key.startsWith("marketing_quality_") || data.key === "marketing_messages_enabled") {
      const { invalidateMarketingQualityCache } = await import("@/lib/server/marketingQuality");
      invalidateMarketingQualityCache();
    }
    return { ok: true };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 9) Dashboard Overview — مؤشّرات أداء + تنبيهات
// ─────────────────────────────────────────────────────────────────────────────
/**
 * يُرجع KPIs الرئيسيّة + قائمة تنبيهات تتطلّب اهتماماً (failed WhatsApp,
 * طلبات دفع معلّقة, سفراء بانتظار الموافقة).
 */
const overviewInput = z.object({
  // فترة ديناميكيّة بأيّام (1 = اليوم، 7 = أسبوع، 30 = شهر، 90 = ٣ أشهر…)
  // أو fromDate/toDate لمدى مخصّص.
  days: z.number().int().min(1).max(365).optional(),
  fromDate: z.string().optional(), // ISO
  toDate: z.string().optional(),
});
export type OverviewInput = z.infer<typeof overviewInput>;

export const getAdminOverview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => overviewInput.parse(input ?? {}))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);

    // نحدّد بداية ونهاية الفترة المطلوبة
    const now = new Date();
    let from: Date;
    let to: Date = now;

    if (data.fromDate) {
      from = new Date(data.fromDate);
      if (data.toDate) to = new Date(data.toDate);
    } else {
      const days = data.days ?? 7;
      if (days === 1) {
        // "اليوم" = منذ منتصف الليل
        from = new Date(now);
        from.setHours(0, 0, 0, 0);
      } else {
        from = new Date(now);
        from.setDate(from.getDate() - days);
      }
    }

    const fromIso = from.toISOString();
    const toIso = to.toISOString();

    const [
      { count: ordersInRange },
      { count: giftsInRange },
      { count: ambassadorsApproved },
      { count: ambassadorsPending },
      { count: failedWhatsapp },
      { count: payoutsPending },
      { data: balances },
      { data: referralEvents },
    ] = await Promise.all([
      supabaseAdmin
        .from("gift_notifications")
        .select("id", { count: "exact", head: true })
        .gte("created_at", fromIso)
        .lte("created_at", toIso),
      supabaseAdmin
        .from("gift_notifications")
        .select("id", { count: "exact", head: true })
        .eq("notify_recipient", true)
        .gte("created_at", fromIso)
        .lte("created_at", toIso),
      supabaseAdmin
        .from("ambassadors")
        .select("id", { count: "exact", head: true })
        .eq("status", "approved"),
      supabaseAdmin
        .from("ambassadors")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      supabaseAdmin
        .from("gift_notifications")
        .select("id", { count: "exact", head: true })
        .not("last_error", "is", null),
      supabaseAdmin
        .from("ambassador_payouts")
        .select("id", { count: "exact", head: true })
        .eq("status", "requested"),
      supabaseAdmin
        .from("ambassador_balances")
        .select("currency, available_credit"),
      // أحداث السفراء خلال الفترة (نقرات + مبيعات + إيراد) — مع ambassador_id للتصنيف
      supabaseAdmin
        .from("ambassador_events")
        .select("event_type, order_total, currency, ambassador_id")
        .gte("created_at", fromIso)
        .lte("created_at", toIso),
    ]);

    // مجموع الرصيد المتاح لكلّ عملة
    const totalsByCurrency = new Map<string, number>();
    for (const b of balances ?? []) {
      const cur = (b as { currency: string }).currency;
      const amt = Number((b as { available_credit: number }).available_credit ?? 0);
      totalsByCurrency.set(cur, (totalsByCurrency.get(cur) ?? 0) + amt);
    }

    // KPIs الإحالة: نقرات + مبيعات + إيراد + CVR
    let referralClicks = 0;
    let referralSales = 0;
    const revenueByCurrency = new Map<string, number>();

    // تجميع أداء السفراء فردياً للوحة الترتيب
    type PerAmb = { clicks: number; sales: number; revenue: number; currency: string | null };
    const perAmbassador = new Map<string, PerAmb>();

    for (const ev of referralEvents ?? []) {
      const t = (ev as { event_type: string }).event_type;
      const ambId = (ev as { ambassador_id: string }).ambassador_id;
      if (!perAmbassador.has(ambId)) {
        perAmbassador.set(ambId, { clicks: 0, sales: 0, revenue: 0, currency: null });
      }
      const row = perAmbassador.get(ambId)!;
      if (t === "click") {
        referralClicks += 1;
        row.clicks += 1;
      }
      if (t === "purchase") {
        referralSales += 1;
        const cur = (ev as { currency: string | null }).currency ?? "AED";
        const amt = Number((ev as { order_total: number | null }).order_total ?? 0);
        revenueByCurrency.set(cur, (revenueByCurrency.get(cur) ?? 0) + amt);
        row.sales += 1;
        row.revenue += amt;
        if (!row.currency) row.currency = cur;
      }
    }
    const conversionRate = referralClicks > 0 ? (referralSales / referralClicks) * 100 : 0;

    // أعلى ١٠ سفراء حسب (المبيعات → الإيراد → النقرات)
    const topAmbIds = Array.from(perAmbassador.entries())
      .filter(([, v]) => v.clicks > 0 || v.sales > 0)
      .sort(([, a], [, b]) => {
        if (b.sales !== a.sales) return b.sales - a.sales;
        if (b.revenue !== a.revenue) return b.revenue - a.revenue;
        return b.clicks - a.clicks;
      })
      .slice(0, 10)
      .map(([id]) => id);

    let topAmbassadors: Array<{
      ambassador_id: string;
      display_name: string;
      code: string;
      clicks: number;
      sales: number;
      revenue: number;
      currency: string | null;
      conversionRate: number;
    }> = [];

    if (topAmbIds.length > 0) {
      const { data: ambRows } = await supabaseAdmin
        .from("ambassadors")
        .select("id, display_name, code")
        .in("id", topAmbIds);
      const ambMap = new Map<string, { display_name: string; code: string }>();
      for (const a of ambRows ?? []) ambMap.set(a.id, { display_name: a.display_name, code: a.code });

      topAmbassadors = topAmbIds.map((id) => {
        const v = perAmbassador.get(id)!;
        const meta = ambMap.get(id);
        return {
          ambassador_id: id,
          display_name: meta?.display_name ?? "—",
          code: meta?.code ?? "—",
          clicks: v.clicks,
          sales: v.sales,
          revenue: v.revenue,
          currency: v.currency,
          conversionRate: v.clicks > 0 ? (v.sales / v.clicks) * 100 : 0,
        };
      });
    }

    return {
      range: { from: fromIso, to: toIso },
      kpis: {
        ordersInRange: ordersInRange ?? 0,
        giftsInRange: giftsInRange ?? 0,
        ambassadorsApproved: ambassadorsApproved ?? 0,
      },
      referral: {
        clicks: referralClicks,
        sales: referralSales,
        conversionRate, // 0..100
        revenueByCurrency: Array.from(revenueByCurrency.entries()).map(
          ([currency, amount]) => ({ currency, amount }),
        ),
        topAmbassadors,
      },
      alerts: {
        ambassadorsPending: ambassadorsPending ?? 0,
        failedWhatsapp: failedWhatsapp ?? 0,
        payoutsPending: payoutsPending ?? 0,
      },
      walletTotals: Array.from(totalsByCurrency.entries()).map(([currency, amount]) => ({
        currency,
        amount,
      })),
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 10) Orders feed موحّد — gift_notifications + roll-up هدايا/سفير
// ─────────────────────────────────────────────────────────────────────────────
const listOrdersInput = z.object({
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(10).max(100).default(50),
  type: z.enum(["all", "gift", "self"]).default("all"),
  whatsapp: z.enum(["all", "sent", "pending", "failed"]).default("all"),
  search: z.string().max(120).optional(),
  fromDate: z.string().optional(), // ISO
  toDate: z.string().optional(),
});
export type ListOrdersInput = z.infer<typeof listOrdersInput>;

export const listOrdersAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => listOrdersInput.parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);

    let q = supabaseAdmin
      .from("gift_notifications")
      .select(
        "id, order_id, order_name, created_at, customer_email, customer_phone, recipient_name, recipient_phone, sender_name, locale, notify_recipient, notified_created_at, notified_delivered_at, last_error",
        { count: "exact" },
      )
      .order("created_at", { ascending: false });

    if (data.type === "gift") q = q.eq("notify_recipient", true);
    if (data.type === "self") q = q.eq("notify_recipient", false);

    if (data.whatsapp === "sent") q = q.not("notified_created_at", "is", null);
    if (data.whatsapp === "failed") q = q.not("last_error", "is", null);
    if (data.whatsapp === "pending")
      q = q.is("notified_created_at", null).is("last_error", null).eq("notify_recipient", true);

    if (data.fromDate) q = q.gte("created_at", data.fromDate);
    if (data.toDate) q = q.lte("created_at", data.toDate);

    if (data.search && data.search.trim()) {
      const s = data.search.trim();
      q = q.or(
        `order_name.ilike.%${s}%,customer_email.ilike.%${s}%,customer_phone.ilike.%${s}%,recipient_name.ilike.%${s}%,recipient_phone.ilike.%${s}%`,
      );
    }

    const from = data.page * data.pageSize;
    const to = from + data.pageSize - 1;
    q = q.range(from, to);

    const { data: rows, count, error } = await q;
    if (error) {
      console.error("[listOrdersAdmin]", error);
      throw new Response(error.message, { status: 400 });
    }

    if (!rows || rows.length === 0) {
      return { rows: [], count: count ?? 0 };
    }

    // ربط بـambassador_events (لو الطلب جاء عبر سفير)
    const orderIds = rows.map((r) => r.order_id).filter(Boolean);
    const { data: ambEvents } = await supabaseAdmin
      .from("ambassador_events")
      .select("order_id, ambassador_id, event_type, order_total, currency")
      .in("order_id", orderIds)
      .eq("event_type", "purchase");

    const ambIds = Array.from(new Set((ambEvents ?? []).map((e) => e.ambassador_id)));
    const { data: ambs } = ambIds.length
      ? await supabaseAdmin
          .from("ambassadors")
          .select("id, code, display_name")
          .in("id", ambIds)
      : { data: [] };

    const ambByOrder = new Map<
      string,
      { code: string; display_name: string; total: number | null; currency: string | null }
    >();
    for (const ev of ambEvents ?? []) {
      const a = (ambs ?? []).find((x) => x.id === ev.ambassador_id);
      if (a && ev.order_id) {
        ambByOrder.set(ev.order_id, {
          code: a.code,
          display_name: a.display_name,
          total: ev.order_total !== null ? Number(ev.order_total) : null,
          currency: ev.currency ?? null,
        });
      }
    }

    // ربط بـgift_redemptions (هل أخذ عيّنة مجّانيّة؟)
    const { data: redemptions } = await supabaseAdmin
      .from("gift_redemptions")
      .select("order_id")
      .in("order_id", orderIds);

    const redeemedOrders = new Set((redemptions ?? []).map((r) => r.order_id));

    // نقاط الولاء الممنوحة للعميل لكلّ طلب
    const { data: loyaltyTxns } = await supabaseAdmin
      .from("customer_loyalty_transactions")
      .select("related_order_id, points, reason")
      .in("related_order_id", orderIds);

    const loyaltyByOrder = new Map<string, number>();
    for (const t of loyaltyTxns ?? []) {
      if (!t.related_order_id || (t.points ?? 0) <= 0) continue;
      loyaltyByOrder.set(
        t.related_order_id,
        (loyaltyByOrder.get(t.related_order_id) ?? 0) + Number(t.points),
      );
    }

    // كاش السفير الممنوح لكلّ طلب (الكسب فقط — موجب)
    const { data: walletTxns } = await supabaseAdmin
      .from("ambassador_wallet_transactions")
      .select("related_order_id, amount, currency, balance_state, txn_type")
      .in("related_order_id", orderIds)
      .gt("amount", 0);

    const ambCashByOrder = new Map<
      string,
      { amount: number; currency: string; state: string }
    >();
    for (const t of walletTxns ?? []) {
      if (!t.related_order_id) continue;
      const prev = ambCashByOrder.get(t.related_order_id);
      ambCashByOrder.set(t.related_order_id, {
        amount: (prev?.amount ?? 0) + Number(t.amount),
        currency: t.currency ?? "AED",
        state: t.balance_state ?? "available",
      });
    }

    // حالات الطلب من جدول orders (ماليّة/تجهيز/إلغاء)
    const { data: ordersMeta } = await supabaseAdmin
      .from("orders")
      .select("shopify_order_id, discount_code, financial_status, fulfillment_status, cancelled_at, cancel_reason")
      .in("shopify_order_id", orderIds);

    const orderStatusByOrder = new Map<
      string,
      {
        financial_status: string | null;
        fulfillment_status: string | null;
        cancelled_at: string | null;
        cancel_reason: string | null;
      }
    >();
    const discountByOrder = new Map<string, string>();
    const allCoupons: string[] = [];
    for (const o of ordersMeta ?? []) {
      if (o.shopify_order_id) {
        orderStatusByOrder.set(o.shopify_order_id, {
          financial_status: o.financial_status ?? null,
          fulfillment_status: o.fulfillment_status ?? null,
          cancelled_at: o.cancelled_at ?? null,
          cancel_reason: o.cancel_reason ?? null,
        });
      }
      if (o.shopify_order_id && o.discount_code) {
        discountByOrder.set(o.shopify_order_id, o.discount_code);
        allCoupons.push(o.discount_code);
      }
    }

    const { data: combinedRedemptions } = allCoupons.length
      ? await supabaseAdmin
          .from("wallet_combined_redemptions")
          .select("coupon_code, total_aed, points_used, points_value_aed, cash_used_aed")
          .in("coupon_code", allCoupons)
      : { data: [] };

    const redemptionByCoupon = new Map<
      string,
      {
        total_aed: number;
        points_used: number;
        points_value_aed: number;
        cash_used_aed: number;
      }
    >();
    for (const c of combinedRedemptions ?? []) {
      if (!c.coupon_code) continue;
      redemptionByCoupon.set(c.coupon_code, {
        total_aed: Number(c.total_aed ?? 0),
        points_used: Number(c.points_used ?? 0),
        points_value_aed: Number(c.points_value_aed ?? 0),
        cash_used_aed: Number(c.cash_used_aed ?? 0),
      });
    }

    return {
      rows: rows.map((r) => {
        const discountCode = r.order_id ? discountByOrder.get(r.order_id) ?? null : null;
        const combined = discountCode ? redemptionByCoupon.get(discountCode) ?? null : null;
        const orderStatus = r.order_id ? orderStatusByOrder.get(r.order_id) ?? null : null;
        return {
        id: r.id,
        order_id: r.order_id,
        order_name: r.order_name,
        created_at: r.created_at,
        customer_email: r.customer_email,
        customer_phone: r.customer_phone,
        recipient_name: r.recipient_name,
        recipient_phone: r.recipient_phone,
        sender_name: r.sender_name,
        locale: r.locale,
        is_gift: r.notify_recipient,
        financial_status: orderStatus?.financial_status ?? null,
        fulfillment_status: orderStatus?.fulfillment_status ?? null,
        cancelled_at: orderStatus?.cancelled_at ?? null,
        cancel_reason: orderStatus?.cancel_reason ?? null,
        whatsapp_status: r.last_error
          ? ("failed" as const)
          : r.notified_delivered_at
            ? ("delivered" as const)
            : r.notified_created_at
              ? ("sent" as const)
              : r.notify_recipient
                ? ("pending" as const)
                : ("none" as const),
        whatsapp_error: r.last_error,
        ambassador: ambByOrder.get(r.order_id) ?? null,
        has_redemption: redeemedOrders.has(r.order_id),
        rewards: {
          loyalty_points: r.order_id ? loyaltyByOrder.get(r.order_id) ?? 0 : 0,
          ambassador_cash: r.order_id ? ambCashByOrder.get(r.order_id) ?? null : null,
          discount_code: discountCode,
          combined_redemption: combined,
        },
        };
      }),
      count: count ?? 0,
    };
  });

// ─────────────────────────────────────────────────────────────────────────────
// ملفّ سفير/عميل موحَّد — يعمل حتّى لو لم يملك حساباً (مشترٍ ضيف بلا user_id)
// ─────────────────────────────────────────────────────────────────────────────
export const getAmbassadorProfileAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => z.object({ ambassadorId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);

    const { data: amb, error } = await supabaseAdmin
      .from("ambassadors")
      .select("*")
      .eq("id", data.ambassadorId)
      .maybeSingle();
    if (error) throw new Response(error.message, { status: 500 });
    if (!amb) throw new Response("Ambassador not found", { status: 404 });

    const uid = (amb.user_id as string | null) ?? null;

    let email: string | null = (amb.external_email as string | null) ?? null;
    if (uid) {
      try {
        const { data: au } = await supabaseAdmin.auth.admin.getUserById(uid);
        email = au?.user?.email ?? email;
      } catch {
        /* ignore */
      }
    }

    const orderCols =
      "id, shopify_order_id, order_name, market, currency, total_amount, is_gift, financial_status, fulfillment_status, shopify_created_at, created_at, cancelled_at, customer_email, customer_phone, visitor_id";

    const [byUser, byEmail, profile, loyalty, carts, events, ambEvents] = await Promise.all([
      uid
        ? supabaseAdmin
            .from("orders")
            .select(orderCols)
            .eq("user_id", uid)
            .order("created_at", { ascending: false })
            .limit(30)
        : Promise.resolve({ data: [] as never[] }),
      email
        ? supabaseAdmin
            .from("orders")
            .select(orderCols)
            .ilike("customer_email", email)
            .order("created_at", { ascending: false })
            .limit(30)
        : Promise.resolve({ data: [] as never[] }),
      uid
        ? supabaseAdmin
            .from("profiles")
            .select("display_name, phone_e164, preferred_market, preferred_locale, created_at")
            .eq("id", uid)
            .maybeSingle()
        : Promise.resolve({ data: null }),
      uid
        ? supabaseAdmin
            .from("customer_loyalty")
            .select("points_balance, lifetime_earned")
            .eq("user_id", uid)
            .maybeSingle()
        : Promise.resolve({ data: null }),
      uid
        ? supabaseAdmin
            .from("cart_snapshots")
            .select("items, items_count, cart_value, cart_currency, market, updated_at")
            .eq("user_id", uid)
            .order("updated_at", { ascending: false })
            .limit(1)
        : Promise.resolve({ data: [] as never[] }),
      uid
        ? supabaseAdmin
            .from("customer_events")
            .select("id, event_type, path, product_handle, product_title, market, created_at")
            .eq("user_id", uid)
            .order("created_at", { ascending: false })
            .limit(40)
        : Promise.resolve({ data: [] as never[] }),
      supabaseAdmin
        .from("ambassador_events")
        .select("id, event_type, created_at, order_id")
        .eq("ambassador_id", data.ambassadorId)
        .order("created_at", { ascending: false })
        .limit(20),
    ]);

    // لا نسمح بفشل صامت: أيّ خطأ استعلام يُرفع بدل إظهار «لا طلبات»
    for (const res of [byUser, byEmail, profile, loyalty, carts, events, ambEvents]) {
      const err = (res as { error?: { message?: string } }).error;
      if (err?.message) throw new Response(err.message, { status: 500 });
    }

    type JsonVal = string | number | boolean | null | JsonVal[] | { [k: string]: JsonVal };
    type AnyRow = Record<string, JsonVal | undefined>;

    const { unionOrders, summarizeOrders, classifyOrder } = await import(
      "@/lib/server/customerOrders.server"
    );
    const orders = unionOrders(
      ((byUser as { data: unknown[] }).data ?? []) as never,
      ((byEmail as { data: unknown[] }).data ?? []) as never,
    ) as unknown as AnyRow[];
    // نُلحق تصنيف الحالة بكلّ طلب حتّى تعرضه الواجهة كما هو
    for (const o of orders) o["status_class"] = classifyOrder(o as never);
    const totals = summarizeOrders(orders as never);

    // الضيف بلا حساب: نستخرج سلّته وأحداثه عبر visitor_id من طلباته
    let guestCart: AnyRow | null = null;
    let guestEvents: AnyRow[] = [];
    if (!uid) {
      const visitorIds = [
        ...new Set(
          orders
            .map((o) => (o["visitor_id"] ? String(o["visitor_id"]) : null))
            .filter((v): v is string => !!v),
        ),
      ];
      if (visitorIds.length) {
        const [gc, ge] = await Promise.all([
          supabaseAdmin
            .from("cart_snapshots")
            .select("items, items_count, cart_value, cart_currency, market, updated_at")
            .in("visitor_id", visitorIds)
            .order("updated_at", { ascending: false })
            .limit(1),
          supabaseAdmin
            .from("customer_events")
            .select("id, event_type, path, product_handle, product_title, market, created_at")
            .in("session_id", visitorIds)
            .order("created_at", { ascending: false })
            .limit(40),
        ]);
        guestCart = ((gc.data ?? [])[0] as AnyRow | undefined) ?? null;
        guestEvents = (ge.data ?? []) as unknown as AnyRow[];
      }
    }

    // سجلّ مشاركة الروابط/الوسائط — نفس مصدر /admin/media-shares
    const sharesQ = supabaseAdmin
      .from("ambassador_media_shares")
      .select("id, share_kind, platform, share_url, media_title, campaign_title, created_at")
      .order("created_at", { ascending: false })
      .limit(30);
    const { data: sharesData, error: sharesErr } = await (uid
      ? sharesQ.or(`ambassador_id.eq.${data.ambassadorId},user_id.eq.${uid}`)
      : sharesQ.eq("ambassador_id", data.ambassadorId));
    if (sharesErr) throw new Response(sharesErr.message, { status: 500 });

    // المفضّلة (للحسابات فقط)
    const wishlist = uid
      ? ((
          await supabaseAdmin
            .from("wishlists")
            .select("id, product_handle, product_title, product_price, product_currency, created_at")
            .eq("user_id", uid)
            .order("created_at", { ascending: false })
            .limit(20)
        ).data ?? [])
      : [];

    return {
      ambassador: amb,
      email,
      hasAccount: Boolean(uid),
      profile: (profile as { data: unknown }).data ?? null,
      loyalty: (loyalty as { data: unknown }).data ?? null,
      cart:
        (((carts as { data: unknown[] }).data ?? [])[0] as AnyRow | undefined) ?? guestCart ?? null,
      orders,
      totals: {
        count: totals.count,
        total: totals.total,
        currency: totals.currency ?? "AED",
        pendingCount: totals.pendingCount,
        pendingTotal: totals.pendingTotal,
        refundedCount: totals.refundedCount,
        cancelledCount: totals.cancelledCount,
        ordersCount: totals.ordersCount,
      },
      events: (((events as { data: unknown[] }).data ?? []) as AnyRow[]).length
        ? (((events as { data: unknown[] }).data ?? []) as AnyRow[])
        : guestEvents,
      wishlist: wishlist as unknown as AnyRow[],
      shares: (sharesData ?? []) as unknown as AnyRow[],
      ambassadorEvents: ((ambEvents as { data: unknown[] }).data ?? []) as AnyRow[],
    };
  });

